/**
 * React Query hooks para Billing.
 *
 * Phase 1: GET/POST `/billing/customer` por tenant.
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { toast } from '@/hooks/use-toast';
import { getApiErrorCode } from '@/utils/api-errors';
import { useBillingBlockStore } from '@/stores/billing-block.store';
import {
  billingService,
  type BillingAccess,
  type BillingAccessState,
  type BillingCustomer,
  type BillingPlan,
  type CreateBillingCustomerDTO,
} from '@/services/billing.service';

export const billingKeys = {
  all: ['billing'] as const,
  access: () => [...billingKeys.all, 'access'] as const,
  customer: () => [...billingKeys.all, 'customer'] as const,
  plans: () => [...billingKeys.all, 'plans'] as const,
};

/** Mensagens amigáveis para códigos de erro do backend de billing. */
const ERROR_MESSAGES: Record<string, string> = {
  AUTHZ_NO_TENANT:
    'Sua conta não está associada a um tenant. Contate o administrador.',
  AUTHZ_NO_ORGANIZATION:
    'Sua conta não está associada a uma organização. Contate o administrador.',
  VALIDATION_DOCUMENT_INVALID:
    'CPF ou CNPJ inválido. Verifique os dígitos informados.',
  VALIDATION_DOCUMENT_TYPE_MISMATCH:
    'O tipo de documento não corresponde ao número informado.',
  CONFLICT_BILLING_CUSTOMER_EXISTS:
    'Já existe um cliente de cobrança para este tenant.',
};

function describeBillingError(error: unknown): string {
  const code = getApiErrorCode(error);
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
  const ax = error as AxiosError<{ message?: string; error?: { message?: string } }>;
  return (
    ax?.response?.data?.error?.message ||
    ax?.response?.data?.message ||
    ax?.message ||
    'Erro ao processar a operação de cobrança.'
  );
}

/** Estados de `accessState` que representam um BLOQUEIO de acesso. */
const BLOCKING_ACCESS_STATES: ReadonlySet<BillingAccessState> = new Set([
  'paywall',
  'org_suspended',
]);

/**
 * Hook: GET /billing/access — estado de billing/owner do tenant.
 *
 * Usado para gatear a aba Faturamento (`isBillingOwner`) e mostrar o estado de
 * billing (trial/paywall/ativo). `staleTime` curto pois o estado pode mudar via
 * webhook (pagamento/inadimplência).
 *
 * **Caminho de DESBLOQUEIO (Epic #884 §3.1 — anti-deadlock):** este endpoint é
 * allowlistado (`/billing/*`), então passa pelo interceptor MESMO com o bloqueio
 * ativo. Quando todas as requests não-allowlistadas estão bloqueadas, é a ÚNICA
 * fonte que consegue LIMPAR o bloqueio: ao reportar `allowed`/`trial` (ex.: após
 * o cliente pagar e o `/billing/access` ser revalidado), limpamos o store →
 * requests voltam a fluir. Quando reporta `paywall`/`org_suspended`, setamos o
 * bloqueio (complementa o set dirigido por 402 no interceptor).
 */
export function useBillingAccess(enabled = true) {
  const query = useQuery<BillingAccess>({
    queryKey: billingKeys.access(),
    queryFn: () => billingService.getBillingAccess(),
    enabled,
    staleTime: 60 * 1000,
  });

  const access = query.data;
  useEffect(() => {
    if (!access) return;
    const { block, clear } = useBillingBlockStore.getState();
    const isBlockingState =
      BLOCKING_ACCESS_STATES.has(access.accessState) ||
      Boolean(access.blockCode);
    if (isBlockingState) {
      block({
        kind: access.accessState === 'org_suspended' ? 'org_suspended' : 'paywall',
        code: access.blockCode,
      });
    } else {
      // allowed / trial → acesso liberado: limpa qualquer bloqueio ativo.
      clear();
    }
  }, [access]);

  return query;
}

/**
 * Hook: GET /billing/plans — catálogo de planos ATIVOS da organização (PR5a).
 *
 * Usado pela tela de Faturamento para montar os cards de plano + o toggle
 * mensal/anual. `staleTime` de 5 min (catálogo muda raramente).
 */
export function useBillingPlans() {
  return useQuery<BillingPlan[]>({
    queryKey: billingKeys.plans(),
    queryFn: () => billingService.listPlans(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook: GET /billing/customer (retorna `null` quando 404).
 */
export function useBillingCustomer() {
  return useQuery<BillingCustomer | null>({
    queryKey: billingKeys.customer(),
    queryFn: () => billingService.getBillingCustomer(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook: POST /billing/customer.
 * Mostra toast de sucesso/erro e invalida o cache do customer.
 */
export function useCreateBillingCustomer() {
  const queryClient = useQueryClient();

  return useMutation<BillingCustomer, unknown, CreateBillingCustomerDTO>({
    mutationFn: (payload) => billingService.createBillingCustomer(payload),
    onSuccess: (customer) => {
      queryClient.setQueryData(billingKeys.customer(), customer);
      queryClient.invalidateQueries({ queryKey: billingKeys.all });
      toast({
        title: 'Cobrança configurada',
        description: 'Cliente de cobrança criado com sucesso.',
      });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Não foi possível salvar',
        description: describeBillingError(error),
      });
    },
  });
}
